import React, { createContext, useState, useContext, useCallback, useEffect } from "react"

// AppContext interface for keyboard and fullscreen state
export interface AppContextType {
    showKeyboard: boolean
    setShowKeyboard: (show: boolean) => void
    keyboardInput: string
    setKeyboardInput: (input: string) => void
    isFullscreen: boolean
    toggleFullscreen: () => void
    keyboardEnabled: boolean
    toggleKeyboardEnabled: () => void
}

// Create the context with undefined initial value
const AppContext = createContext<AppContextType | undefined>(undefined)

// Hook to use the app context
export const useAppContext = () => {
    const context = useContext(AppContext)
    if (context === undefined) {
        throw new Error("useAppContext must be used within an AppProvider")
    }
    return context
}

// App provider component that provides global state
export const AppProvider = ({ children }: { children: React.ReactNode }) => {
    const [showKeyboard, setShowKeyboard] = useState(false)
    const [keyboardInput, setKeyboardInput] = useState("")
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [keyboardEnabled, setKeyboardEnabled] = useState(false) // Default to disabled

    // Function to toggle keyboard enabled state
    const toggleKeyboardEnabled = useCallback(() => {
        setKeyboardEnabled((prev) => !prev)
    }, [])

    // Auto show/hide keyboard based on focus (only if keyboard is enabled)
    useEffect(() => {
        if (!keyboardEnabled) return

        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement

            // Check if the focused element is an input or textarea
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.getAttribute("contenteditable") === "true")
            ) {
                setShowKeyboard(true)
            }
        }

        const handleFocusOut = (e: FocusEvent) => {
            // Only hide if keyboard is enabled
            if (keyboardEnabled) {
                // Check if the next active element is also an input
                const relatedTarget = e.relatedTarget as HTMLElement
                if (
                    !relatedTarget ||
                    (relatedTarget.tagName !== "INPUT" &&
                        relatedTarget.tagName !== "TEXTAREA" &&
                        relatedTarget.getAttribute("contenteditable") !== "true" &&
                        !relatedTarget.closest(".virtual-keyboard") && // Don't hide when focusing on keyboard
                        !(relatedTarget.tagName === "BUTTON" && relatedTarget.getAttribute("aria-label") === "Toggle keyboard"))
                ) {
                    // Only hide if we're not focusing another input
                    setShowKeyboard(false)
                }
            }
        }

        // Handle clicks outside of input elements and keyboard
        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement

            // If keyboard is enabled and we click outside inputs, hide keyboard
            if (
                keyboardEnabled &&
                target &&
                target.tagName !== "INPUT" &&
                target.tagName !== "TEXTAREA" &&
                target.getAttribute("contenteditable") !== "true" &&
                !target.closest(".virtual-keyboard") && // Don't hide when clicking the keyboard itself
                !(target.tagName === "BUTTON" && target.getAttribute("aria-label") === "Toggle keyboard")
            ) {
                setShowKeyboard(false)
            }
        }

        // Add event listeners
        document.addEventListener("focusin", handleFocusIn)
        document.addEventListener("focusout", handleFocusOut)
        document.addEventListener("click", handleDocumentClick)

        return () => {
            document.removeEventListener("focusin", handleFocusIn)
            document.removeEventListener("focusout", handleFocusOut)
            document.removeEventListener("click", handleDocumentClick)
        }
    }, [keyboardEnabled, setShowKeyboard])

    // Enhanced keyboard support for OTP and special inputs
    useEffect(() => {
        // Helper to manually distribute keyboard events to active elements
        const enhanceVirtualKeyboardSupport = () => {
            // OTP Inputs typically have pattern="[0-9]*" or inputMode="numeric"
            const virtualKeyHandler = (e: Event) => {
                const customEvent = e as CustomEvent
                if (!customEvent.detail || !customEvent.detail.key) return

                const key = customEvent.detail.key

                // Get the active element
                const activeElement = document.activeElement as HTMLElement

                // Only process for inputs
                if (!activeElement || (activeElement.tagName !== "INPUT" && activeElement.tagName !== "TEXTAREA")) {
                    return
                }

                // Special handling for OTP inputs with specific attributes
                // This helps with inputs that might not properly respond to simulated keyboard events
                const inputElement = activeElement as HTMLInputElement

                // Check if this is likely an OTP input
                const isOTPInput =
                    inputElement.type === "text" &&
                    (inputElement.pattern === "[0-9]*" || inputElement.inputMode === "numeric") &&
                    (inputElement.maxLength === 1 || // Single digit input
                        // Check parent container for class names suggesting OTP
                        inputElement.parentElement?.className.includes("otp") ||
                        inputElement.parentElement?.className.includes("verification"))

                if (isOTPInput && /^[0-9]$/.test(key)) {
                    // Force value update for OTP
                    inputElement.value = key
                    inputElement.dispatchEvent(new Event("input", { bubbles: true }))
                    inputElement.dispatchEvent(new Event("change", { bubbles: true }))

                    // Try to find and focus the next input
                    // Common pattern: OTP inputs are siblings in the same container
                    const container = inputElement.parentElement?.parentElement

                    if (container) {
                        // Find all input elements in this container
                        const allInputs = Array.from(container.querySelectorAll("input"))
                        const currentIndex = allInputs.indexOf(inputElement)

                        if (currentIndex >= 0 && currentIndex < allInputs.length - 1) {
                            // Focus the next input
                            setTimeout(() => {
                                allInputs[currentIndex + 1].focus()
                            }, 10)
                        }
                    }
                }
            }

            // Listen for our custom virtual keyboard events
            document.addEventListener("virtualKeyPress", virtualKeyHandler)

            return () => {
                document.removeEventListener("virtualKeyPress", virtualKeyHandler)
            }
        }

        // Setup enhanced keyboard support
        const cleanup = enhanceVirtualKeyboardSupport()

        return cleanup
    }, [])

    // Handle fullscreen toggling
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch((err) => {
                            })
            setIsFullscreen(true)
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen()
                setIsFullscreen(false)
            }
        }
    }, [])

    // Update fullscreen state when it changes externally
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }

        document.addEventListener("fullscreenchange", handleFullscreenChange)
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange)
        }
    }, [])

    const value = {
        showKeyboard,
        setShowKeyboard,
        keyboardInput,
        setKeyboardInput,
        isFullscreen,
        toggleFullscreen,
        keyboardEnabled,
        toggleKeyboardEnabled,
    }

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}